import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import { useCreateSetupIntent } from '@/lib/api/hooks';
import { getStripe } from '@/lib/stripe';
import type { SetupIntentResponse } from '@/types/api';

/**
 * Add-a-card flow for the portal checkout. Mounts Stripe Elements against a
 * backend-minted SetupIntent (off-session card), confirms it, and reports the
 * new PaymentMethod id back to the cart so it can be auto-selected.
 *
 * The publishable key comes from the SetupIntent response (not a VITE env) so
 * Stripe.js always matches the backend's active Stripe mode (test vs live).
 */
export function AddCardForm({
  onAdded,
  onCancel,
}: {
  onAdded: (pmId: string | null) => void;
  onCancel: () => void;
}) {
  const createSetupIntent = useCreateSetupIntent();
  const [intent, setIntent] = useState<SetupIntentResponse | null>(null);
  const [setupError, setSetupError] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    createSetupIntent
      .mutateAsync()
      .then(setIntent)
      .catch(() => {
        setSetupError(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (setupError || createSetupIntent.isError) {
    return (
      <div className="flex flex-col gap-10 p-15 bg-[var(--surface-sunken)]">
        <p role="alert" className="type-body-sm text-danger">
          Couldn't start the card form. Please try again.
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="type-label-sm text-ink-muted hover:text-ink self-start"
        >
          Cancel
        </button>
      </div>
    );
  }

  if (!intent) {
    return (
      <div className="flex items-center gap-10 p-15 bg-[var(--surface-sunken)] type-body-sm text-ink-secondary">
        <Loader2 size={14} className="animate-spin" /> Loading card form…
      </div>
    );
  }

  return (
    <Elements
      stripe={getStripe(intent.publishable_key)}
      options={{ clientSecret: intent.client_secret }}
    >
      <AddCardInner onAdded={onAdded} onCancel={onCancel} />
    </Elements>
  );
}

function AddCardInner({
  onAdded,
  onCancel,
}: {
  onAdded: (pmId: string | null) => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(
        confirmError.message ?? 'Could not save card. Please try again.',
      );
      setSubmitting(false);
      return;
    }

    const pm = setupIntent?.payment_method;
    const pmId = typeof pm === 'string' ? pm : (pm?.id ?? null);
    // Refresh the saved-card list so the new card shows up, then hand the id
    // back to the cart for auto-selection.
    await qc.invalidateQueries({ queryKey: ['me', 'payment-methods'] });
    onAdded(pmId);
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-12 p-15 bg-[var(--surface-sunken)]"
    >
      {/*
        wallets: Apple Pay / Google Pay surface as express buttons at the top of
        the PaymentElement on supported devices (Safari/iOS for Apple Pay,
        Chrome/Android for Google Pay). Confirming via a wallet saves the
        underlying card as a reusable off-session PaymentMethod, so the
        subscription charge works exactly like a typed card.

        Requirements for Apple Pay to actually appear: HTTPS + the portal's
        domain registered in the Stripe Dashboard (Settings → Payment methods →
        Apple Pay → add `portal.systemlabs.com`). It will NOT show on plain
        http://localhost.
      */}
      <PaymentElement
        options={{
          layout: 'tabs',
          wallets: { applePay: 'auto', googlePay: 'auto' },
        }}
      />
      {error && (
        <div role="alert" className="type-body-sm text-danger">
          {error}
        </div>
      )}
      <div className="flex items-center gap-10">
        <button
          type="submit"
          disabled={!stripe || submitting}
          className="flex items-center gap-8 h-40 px-15 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] type-body-sm hover:opacity-90 disabled:opacity-60"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {submitting ? 'Saving…' : 'Save card'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="type-label-sm text-ink-muted hover:text-ink disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
